import "@/App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import LoginPage       from "@/components/LoginPage";
import SuccessPage      from "@/pages/SuccessPage";
import AuthCallback     from "@/components/AuthCallback";
import UpdateBanner     from "@/components/UpdateBanner";
import AppLayout        from "@/components/AppLayout";
import ProtectedRoute   from "@/components/ProtectedRoute";
import PageShell        from "@/components/PageShell";
import SessionExpiryManager from "@/components/SessionExpiryManager";
import GlobalDocumentNotice from "@/components/wizard/GlobalDocumentNotice";
import { PermissionsProvider } from "@/contexts/PermissionsContext";
import { landingPath } from "@/lib/landing";
import { wikiCategoryPath, isWikiCategorySegment } from "@/lib/wikiUrl";

// ── Lazy-loaded pages (reduces initial bundle size) ───────────────────────────
const TimelinePage     = lazy(() => import("@/pages/TimelinePage"));
const SettingsPage     = lazy(() => import("@/pages/SettingsPage"));
const JournalPage      = lazy(() => import("@/pages/JournalPage"));
const UploadPage       = lazy(() => import("@/pages/UploadPage"));
const DatabasePage     = lazy(() => import("@/pages/DatabasePage"));
const InformationPage  = lazy(() => import("@/pages/InformationPage"));
const ClientsPage      = lazy(() => import("@/pages/ClientsPage"));
const ContactsPage     = lazy(() => import("@/pages/ContactsPage"));
const NotesPage        = lazy(() => import("@/pages/NotesPage"));
const WizardsPage      = lazy(() => import("@/pages/WizardsPage"));
const UploadTaskWizard = lazy(() => import("@/pages/UploadTaskWizard"));
const TestMockupsPage  = lazy(() => import("@/pages/TestMockupsPage"));
const EmailPage        = lazy(() => import("@/pages/EmailPage"));
const CalendarPage     = lazy(() => import("@/pages/CalendarPage"));
const MeetingPage      = lazy(() => import("@/pages/MeetingPage"));
const BookingConfirmedPage = lazy(() => import("@/pages/BookingConfirmedPage"));
const AutomationPage   = lazy(() => import("@/pages/AutomationPage"));
const BillingPage      = lazy(() => import("@/pages/BillingPage"));
const DisclosuresPage  = lazy(() => import("@/pages/DisclosuresPage"));
const DIYWizardsPage   = lazy(() => import("@/pages/DIYWizardsPage"));
const OnboardingPage   = lazy(() => import("@/pages/OnboardingPage"));
const MagicLoginPage   = lazy(() => import("@/pages/MagicLoginPage"));
const WizardLinkPage   = lazy(() => import("@/pages/WizardLinkPage"));

// ── New public pages ──────────────────────────────────────────────────────────
const HomePage         = lazy(() => import("@/pages/HomePage"));
const DIYPage          = lazy(() => import("@/pages/DIYPage"));
const WikiHome         = lazy(() => import("@/pages/wiki/WikiHome"));
const WikiPage         = lazy(() => import("@/pages/wiki/WikiPage"));
const WikiCategory     = lazy(() => import("@/pages/wiki/WikiCategory"));
const WikiAbout        = lazy(() => import("@/pages/wiki/WikiAbout"));
const WikiAdmin        = lazy(() => import("@/pages/wiki/WikiAdmin"));
const ForumPage        = lazy(() => import("@/pages/forum/ForumPage"));
const ForumPostPage    = lazy(() => import("@/pages/forum/ForumPostPage"));
const CalculatorsLanding       = lazy(() => import("@/pages/calculators/CalculatorsLanding"));
const AvailabilityPage         = lazy(() => import("@/pages/AvailabilityPage"));
const ChildSupportCalculator   = lazy(() => import("@/pages/calculators/ChildSupportCalculator"));
const AlimonyCalculator        = lazy(() => import("@/pages/calculators/AlimonyCalculator"));
const CustodyCalculator        = lazy(() => import("@/pages/calculators/CustodyCalculator"));
const PropertyDivisionCalculator = lazy(() => import("@/pages/calculators/PropertyDivisionCalculator"));
const CalculatorPage           = lazy(() => import("@/pages/CalculatorPage"));
const ServiceLandingPage       = lazy(() => import("@/pages/services/ServiceLandingPage"));
const ServicesIndexPage        = lazy(() => import("@/pages/services/ServicesIndexPage"));
const OfferLandingPage         = lazy(() => import("@/pages/landing/OfferLandingPage"));

// ── Loading fallback ──────────────────────────────────────────────────────────
const PageLoader = () => (
  <div className="flex items-center justify-center h-screen bg-gray-50">
    <Loader2 size={24} className="animate-spin text-gray-400" />
  </div>
);

// ── Generic authenticated page ────────────────────────────────────────────────
const Page = ({ title }) => (
  <ProtectedRoute>
    <AppLayout>
      <PageShell title={title} />
    </AppLayout>
  </ProtectedRoute>
);

// ── Router ────────────────────────────────────────────────────────────────────
// Session 294 — the wiki addresses pages the way Wikipedia does:
// `/wiki/Durham_County_Courthouse` for an article, `/wiki/Category:Counties`
// for a listing. Both live on the same `/wiki/:name` route because React
// Router can't express a `Category:` prefix inside one path segment, so the
// namespace is resolved here. The old `/wiki/category/<slug>` URLs redirect.
const WikiNamespaceRoute = () => {
  const { name } = useParams();
  return isWikiCategorySegment(name) ? <WikiCategory /> : <WikiPage />;
};

const LegacyWikiCategoryRedirect = () => {
  const { category } = useParams();
  return <Navigate to={wikiCategoryPath(category)} replace />;
};

// Session 289 — /tasks/:taskId deep links now open the task on the Planner.
const TasksDeepLinkRedirect = () => {
  const { taskId } = useParams();
  return <Navigate to={`/planner?task=${encodeURIComponent(taskId || "")}`} replace />;
};

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* ── Public pages ──────────────────────────────────── */}
        <Route path="/"        element={<HomePage />} />
        <Route path="/login"   element={<LoginPage />} />
        <Route path="/firm"    element={<Navigate to="/login" replace />} />
        <Route path="/client"  element={<Navigate to="/login" replace />} />
        <Route path="/compass" element={<Navigate to="/login" replace />} />
        <Route path="/DIY"     element={<DIYPage />} />
        <Route path="/success"    element={<SuccessPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/magic"      element={<MagicLoginPage />} />
        {/* Session 279 — LogicEngine `continue link` / `wizard link` landing.
            Renders while signed out so it can bounce through /login?next=… */}
        <Route path="/wizard-link" element={<WizardLinkPage />} />
        <Route path="/availability" element={<AvailabilityPage />} />
        {/* Session 304 — Cal.com's "Redirect on booking" target. Public and
            event-agnostic: it renders whatever booking Cal.com forwards. */}
        <Route path="/booking-confirmed" element={<BookingConfirmedPage />} />

        {/* ── Fee Calculator ────────────────────────────────── */}
        <Route path="/calculator"                         element={<CalculatorPage />} />

        {/* ── Calculators ───────────────────────────────────── */}
        <Route path="/calculators"                        element={<CalculatorsLanding />} />
        <Route path="/calculators/child-support"          element={<ChildSupportCalculator />} />
        <Route path="/calculators/alimony"                element={<AlimonyCalculator />} />
        <Route path="/calculators/custody"                element={<CustodyCalculator />} />
        <Route path="/calculators/property-division"      element={<PropertyDivisionCalculator />} />

        {/* ── Services (public practice-area landing pages) ─── */}
        <Route path="/services"                           element={<ServicesIndexPage />} />
        {/* Every practice area renders through the firm-supplied landing
            layout; copy per slug lives in data/services.js + serviceLanding.js. */}
        <Route path="/services/:slug"                     element={<ServiceLandingPage />} />

        {/* ── Focused billing-model landing pages (no site nav) ─── */}
        <Route path="/payment-plan"                       element={<OfferLandingPage />} />
        <Route path="/flat-fee"                           element={<OfferLandingPage />} />

        {/* ── Wiki ──────────────────────────────────────────── */}
        <Route path="/wiki"                      element={<WikiHome />} />
        <Route path="/wiki/about"                element={<WikiAbout />} />
        <Route path="/wiki/admin"                element={<WikiAdmin />} />
        <Route path="/wiki/category/:category"   element={<LegacyWikiCategoryRedirect />} />
        <Route path="/wiki/:name"                element={<WikiNamespaceRoute />} />

        {/* ── Forum ─────────────────────────────────────────── */}
        <Route path="/forum"          element={<ForumPage />} />
        <Route path="/forum/:postId"  element={<ForumPostPage />} />

        {/* ── Shared portal pages (URL independent of user type) ── */}
        {/* Legacy /dashboard removed — redirect to the role-based landing */}
        <Route path="/dashboard"   element={<Navigate to={landingPath()} replace />} />
        <Route path="/planner"    element={<TimelinePage />} />
        <Route path="/timeline"   element={<Navigate to="/planner" replace />} />
        <Route path="/journal"     element={<JournalPage />} />
        <Route path="/upload"      element={<UploadPage />} />
        <Route path="/disclosures" element={<DisclosuresPage />} />
        <Route path="/information" element={<InformationPage />} />
        <Route path="/billing"     element={<BillingPage />} />
        <Route path="/settings"    element={<SettingsPage />} />
        <Route path="/database"   element={<DatabasePage />} />

        {/* ── Lawyer-specific pages ─────────────────────────── */}
        <Route path="/email"      element={<EmailPage />} />
        <Route path="/calendar"   element={<CalendarPage />} />
        <Route path="/meeting"    element={<MeetingPage />} />
        {/* Session 288/289 — /tasks and /meetings folded into the Planner.
            Old links (emails, follow-ups, notifications) keep working. */}
        <Route path="/meetings"   element={<Navigate to="/planner" replace />} />
        <Route path="/tasks"      element={<Navigate to="/planner" replace />} />
        <Route path="/tasks/:taskId/upload" element={<UploadTaskWizard />} />
        <Route path="/tasks/:taskId" element={<TasksDeepLinkRedirect />} />
        <Route path="/test"       element={<TestMockupsPage />} />
        <Route path="/notes"      element={<NotesPage />} />
        <Route path="/notes/:noteId" element={<NotesPage />} />
        <Route path="/wizards"    element={<WizardsPage />} />
        <Route path="/wizards/:wizardId" element={<WizardsPage />} />
        <Route path="/wizards/:wizardId/sessions/:sessionId" element={<WizardsPage />} />
        <Route path="/contacts"   element={<ContactsPage />} />
        <Route path="/clients"    element={<ClientsPage />} />
        <Route path="/clients/:clientId" element={<ClientsPage />} />
        <Route path="/leads"      element={<Page title="Leads"      />} />
        <Route path="/automation" element={<AutomationPage />} />

        {/* ── DIY module pages ──────────────────────────────── */}
        <Route path="/diy"         element={<Page title="DIY" />} />
        <Route path="/DIYWizards"  element={<DIYWizardsPage />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <PermissionsProvider>
        <UpdateBanner />
        <AppRouter />
        <SessionExpiryManager />
        {/* Bottom-right card for Logic Engine document renders — app-wide so
            it shows on any screen, with or without a button press. */}
        <GlobalDocumentNotice />
        <Toaster richColors position="bottom-right" />
      </PermissionsProvider>
    </BrowserRouter>
  );
}

export default App;
